import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOrgContext } from '@/hooks/useOrgContext';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

// ============================================================================
// Preference card form — create (/preference-cards/new), edit
// (/preference-cards/:cardId/edit), and clone (/preference-cards/new?clone=ID).
// Repairs the previously-broken /preference-cards/new route and the Clone/Edit
// TODO stubs on the detail route. Standalone dark-HUD layout to match the
// existing preference-cards screens.
// ============================================================================

interface CardForm {
    provider_id: string;
    procedure_name: string;
    procedure_cpt_code: string;
    procedure_category: string;
    patient_position: string;
    preferred_anesthesia_type: string;
    estimated_duration_minutes: string;
    minimum_staff_required: string;
    equipment_text: string;
    supplies_text: string;
    medications_text: string;
    special_instructions: string;
    table_setup_notes: string;
    is_active: boolean;
}

const EMPTY: CardForm = {
    provider_id: '', procedure_name: '', procedure_cpt_code: '', procedure_category: '',
    patient_position: '', preferred_anesthesia_type: '', estimated_duration_minutes: '',
    minimum_staff_required: '', equipment_text: '', supplies_text: '', medications_text: '',
    special_instructions: '', table_setup_notes: '', is_active: true,
};

function linesToArray(text: string): string[] {
    return text.split('\n').map((s) => s.trim()).filter(Boolean);
}
function arrayToLines(v: unknown): string {
    if (!Array.isArray(v)) return '';
    return v.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join('\n');
}

export function PreferenceCardFormRoute() {
    const navigate = useNavigate();
    const { cardId } = useParams<{ cardId: string }>();
    const [params] = useSearchParams();
    const cloneId = params.get('clone');
    const { org_id } = useOrgContext();

    const isEdit = !!cardId;
    const sourceId = cardId ?? cloneId ?? null; // record to prefill from
    const mode: 'new' | 'edit' | 'clone' = isEdit ? 'edit' : cloneId ? 'clone' : 'new';

    const [form, setForm] = useState<CardForm>(EMPTY);
    const [loading, setLoading] = useState(!!sourceId);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        if (!sourceId) return;
        (async () => {
            setLoading(true);
            const { data, error: e } = await supabase
                .from('surgeon_preference_cards').select('*').eq('preference_card_id', sourceId).single();
            if (cancelled) return;
            if (e || !data) { setError('Could not load the source card.'); setLoading(false); return; }
            const d = data as Record<string, unknown>;
            setForm({
                provider_id: String(d.provider_id ?? ''),
                procedure_name: mode === 'clone' ? `${d.procedure_name ?? ''} (copy)` : String(d.procedure_name ?? ''),
                procedure_cpt_code: String(d.procedure_cpt_code ?? ''),
                procedure_category: String(d.procedure_category ?? ''),
                patient_position: String(d.patient_position ?? ''),
                preferred_anesthesia_type: String(d.preferred_anesthesia_type ?? ''),
                estimated_duration_minutes: d.estimated_duration_minutes != null ? String(d.estimated_duration_minutes) : '',
                minimum_staff_required: d.minimum_staff_required != null ? String(d.minimum_staff_required) : '',
                equipment_text: arrayToLines(d.equipment_list),
                supplies_text: arrayToLines(d.supplies_list),
                medications_text: arrayToLines(d.medications_list),
                special_instructions: String(d.special_instructions ?? ''),
                table_setup_notes: String(d.table_setup_notes ?? ''),
                is_active: d.is_active !== false,
            });
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [sourceId, mode]);

    const canSave = useMemo(
        () => form.procedure_name.trim() && form.provider_id.trim() && !saving,
        [form.procedure_name, form.provider_id, saving]
    );

    function set<K extends keyof CardForm>(key: K, value: CardForm[K]) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    async function save() {
        if (!canSave) return;
        setSaving(true); setError(null);
        const payload = {
            provider_id: Number(form.provider_id),
            procedure_name: form.procedure_name.trim(),
            procedure_cpt_code: form.procedure_cpt_code.trim() || null,
            procedure_category: form.procedure_category.trim() || null,
            patient_position: form.patient_position.trim() || null,
            preferred_anesthesia_type: form.preferred_anesthesia_type.trim() || null,
            estimated_duration_minutes: form.estimated_duration_minutes ? Number(form.estimated_duration_minutes) : null,
            minimum_staff_required: form.minimum_staff_required ? Number(form.minimum_staff_required) : null,
            equipment_list: linesToArray(form.equipment_text),
            supplies_list: linesToArray(form.supplies_text),
            medications_list: linesToArray(form.medications_text),
            special_instructions: form.special_instructions.trim() || null,
            table_setup_notes: form.table_setup_notes.trim() || null,
            is_active: form.is_active,
        };

        try {
            if (mode === 'edit' && cardId) {
                const { error: e } = await supabase
                    .from('surgeon_preference_cards').update(payload).eq('preference_card_id', cardId);
                if (e) { setError(`Save failed: ${e.message}`); return; }
                navigate(`/preference-cards/${cardId}`);
            } else {
                if (!org_id) { setError('No organization context — cannot create a card.'); return; }
                const { data, error: e } = await supabase
                    .from('surgeon_preference_cards')
                    .insert({ ...payload, org_id, times_used: 0 })
                    .select('preference_card_id').single();
                if (e || !data) { setError(`Create failed: ${e?.message ?? 'unknown'}`); return; }
                navigate(`/preference-cards/${(data as { preference_card_id: number }).preference_card_id}`);
            }
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="min-h-full bg-pt-darker text-pt-text">
            <header className="border-b border-pt-border/60">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => navigate(isEdit ? `/preference-cards/${cardId}` : '/preference-cards')}
                            className="text-pt-text-muted hover:text-pt-gold"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-xl font-display font-semibold">
                            {mode === 'edit' ? 'Edit preference card' : mode === 'clone' ? 'Clone preference card' : 'New preference card'}
                        </h1>
                    </div>
                    <button
                        type="button"
                        onClick={save}
                        disabled={!canSave}
                        className="flex items-center gap-2 bg-pt-gold text-pt-darker px-4 py-2 rounded-md font-semibold disabled:opacity-50 [&>svg]:w-4 [&>svg]:h-4"
                    >
                        {saving ? <Loader2 className="animate-spin" /> : <Save />} Save
                    </button>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-6 py-6">
                {loading ? (
                    <div className="text-center py-16"><div className="pt-spinner mx-auto mb-3" /><p className="text-pt-text-muted">Loading…</p></div>
                ) : (
                    <div className="space-y-5">
                        {error && <div className="text-sm text-pt-red py-2 px-3 bg-pt-red/10 border-l-2 border-pt-red rounded">{error}</div>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Text label="Procedure name *" value={form.procedure_name} onChange={(v) => set('procedure_name', v)} />
                            <Text label="Provider ID *" value={form.provider_id} onChange={(v) => set('provider_id', v.replace(/\D/g, ''))} />
                            <Text label="CPT code" value={form.procedure_cpt_code} onChange={(v) => set('procedure_cpt_code', v)} />
                            <Text label="Category" value={form.procedure_category} onChange={(v) => set('procedure_category', v)} />
                            <Text label="Patient position" value={form.patient_position} onChange={(v) => set('patient_position', v)} />
                            <Text label="Anesthesia type" value={form.preferred_anesthesia_type} onChange={(v) => set('preferred_anesthesia_type', v)} />
                            <Text label="Est. duration (min)" value={form.estimated_duration_minutes} onChange={(v) => set('estimated_duration_minutes', v.replace(/\D/g, ''))} />
                            <Text label="Minimum staff" value={form.minimum_staff_required} onChange={(v) => set('minimum_staff_required', v.replace(/\D/g, ''))} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Area label="Equipment (one per line)" value={form.equipment_text} onChange={(v) => set('equipment_text', v)} />
                            <Area label="Supplies (one per line)" value={form.supplies_text} onChange={(v) => set('supplies_text', v)} />
                            <Area label="Medications (one per line)" value={form.medications_text} onChange={(v) => set('medications_text', v)} />
                        </div>

                        <Area label="Special instructions" value={form.special_instructions} onChange={(v) => set('special_instructions', v)} rows={4} />
                        <Area label="Table setup notes" value={form.table_setup_notes} onChange={(v) => set('table_setup_notes', v)} rows={3} />

                        <label className="flex items-center gap-2 text-sm text-pt-text-muted">
                            <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
                            Active
                        </label>
                    </div>
                )}
            </div>
        </div>
    );
}

function Text({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    return (
        <label className="text-xs uppercase tracking-wide text-pt-text-muted">
            {label}
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full px-3 py-2 bg-pt-dark border border-pt-input-border rounded-md text-sm text-pt-text normal-case focus:outline-none focus:border-pt-gold"
            />
        </label>
    );
}

function Area({ label, value, onChange, rows = 5 }: { label: string; value: string; onChange: (v: string) => void; rows?: number }) {
    return (
        <label className="text-xs uppercase tracking-wide text-pt-text-muted block">
            {label}
            <textarea
                value={value}
                rows={rows}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full px-3 py-2 bg-pt-dark border border-pt-input-border rounded-md text-sm text-pt-text normal-case focus:outline-none focus:border-pt-gold"
            />
        </label>
    );
}
